/**
 * SummaryStep.js — Assessment Wizard, Screen 12 "Summary & Review"
 *
 * Unlike every other step, this screen collects nothing new — it reviews
 * everything captured across Screens 2-11 and lets the user jump back to
 * any section via Edit, per FSD: "Each section links back to its screen;
 * editing and returning to Summary preserves all other answers (no
 * reset)." That "no reset" guarantee is automatic here: SummaryStep never
 * mutates the wizard's lifted state, it only reads and displays it, so
 * editing a section and returning changes nothing else.
 *
 * The Generate Diet Plan button triggers UC-02. Unlike every other step's
 * Next button, it doesn't call onNext synchronously: it first submits the
 * full assessment to the /generateDietPlan API (SummaryStep.api.js) and
 * only calls onNext once that request succeeds. If the request fails, the
 * user stays on this screen with an error and can retry — onNext only
 * ever fires after a confirmed save, so the wizard can't advance the user
 * past a plan that was never actually generated.
 */

import React, { useCallback, useMemo, useState } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useSummaryAnalytics from "./useSummaryAnalytics";
import { validateSummary } from "./SummaryStep.validation";
import { submitAssessment } from "./SummaryStep.api";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import { SECTION_CONFIGS } from "./SummaryStep.constants";
import {
  SectionStack,
  SectionCard,
  SectionHeader,
  SectionTitle,
  EditButton,
  RowGrid,
  Row,
  RowLabel,
  RowValue,
  EmptyState,
  ValidationNotice,
  ApiErrorNotice,
} from "./SummaryStep.styles";

const STEP_NUMBER = STEP_NUMBERS.SUMMARY;

// Prettifies an unknown data shape into label/value rows — used for any
// SECTION_CONFIGS entry that doesn't have a dedicated `formatter` yet
// (Activity, Medical, Food Preferences, Likes, Dislikes — see
// SummaryStep.constants.js for why). Handles the common cases: arrays,
// booleans, empty values, and camelCase keys.
function autoFormatFields(data) {
  if (data === null || data === undefined) return [];
  if (Array.isArray(data)) {
    return data.length ? [{ label: "Selected", value: data.join(", ") }] : [];
  }
  if (typeof data !== "object") {
    return [{ label: "Value", value: String(data) }];
  }

  return Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => ({
      label: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .trim(),
      value: Array.isArray(value)
        ? value.join(", ") || "None"
        : typeof value === "boolean"
        ? value
          ? "Yes"
          : "No"
        : String(value),
    }));
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId - Anonymous session id from Screen 1.
 * @param {object}   [props.basicInfo]        - Screen 2 data.
 * @param {string}   [props.goal]             - Screen 3 data.
 * @param {object}   [props.lifestyle]        - Screen 4 data.
 * @param {object}   [props.activity]         - Screen 5 data.
 * @param {object}   [props.medical]          - Screen 6 data.
 * @param {object}   [props.foodPreferences]  - Screen 7 data.
 * @param {object}   [props.likes]            - Screen 8 data (optional).
 * @param {object}   [props.dislikes]         - Screen 9 data (optional).
 * @param {object}   [props.mealSchedule]     - Screen 10 data.
 * @param {object}   [props.womensHealth]     - Screen 11 data (female only).
 * @param {boolean}  props.isFemale           - Gates the Women's Health section.
 * @param {function} props.onPrevious         - Go back to Screen 10 or 11
 *   (whichever the user actually came from — the wizard decides).
 * @param {function} props.onNext             - Called only after
 *   /generateDietPlan responds successfully; advances past UC-02.
 * @param {function} props.onNavigateToStep   - Called with a step number
 *   (from assessmentConfig's STEP_NUMBERS) when an Edit button is clicked.
 */
const SummaryStep = ({
  sessionId,
  basicInfo,
  goal,
  lifestyle,
  activity,
  medical,
  foodPreferences,
  likes,
  dislikes,
  mealSchedule,
  womensHealth,
  isFemale,
  onPrevious,
  onNext,
  onNavigateToStep,
}) => {
  const analytics = useSummaryAnalytics();

  const dataByKey = {
    basicInfo,
    goal,
    lifestyle,
    activity,
    medical,
    foodPreferences,
    likes,
    dislikes,
    mealSchedule,
    womensHealth,
  };

  const { isValid, errors } = useMemo(
    () =>
      validateSummary({
        basicInfo,
        goal,
        lifestyle,
        activity,
        medical,
        foodPreferences,
        mealSchedule,
        womensHealth,
        isFemale,
      }),
    [basicInfo, goal, lifestyle, activity, medical, foodPreferences, mealSchedule, womensHealth, isFemale]
  );

  const visibleSections = useMemo(
    () => SECTION_CONFIGS.filter((section) => !(section.femaleOnly && !isFemale)),
    [isFemale]
  );

  // Submission state for the /generateDietPlan call. Kept local — nothing
  // else in the wizard needs to know the request is in flight.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleEditSection = useCallback(
    (section) => {
      analytics.trackEditSection(sessionId, section.key);
      onNavigateToStep?.(section.stepNumber);
    },
    [analytics, sessionId, onNavigateToStep]
  );

  const handlePrevious = useCallback(() => {
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleGeneratePlan = useCallback(async () => {
    if (!isValid) {
      // Defensive guard — Generate Diet Plan is already disabled via
      // nextDisabled below, this only fires if something calls onNext
      // programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }
    // Guard against a double-click firing a second request while the
    // first is still in flight (button is also disabled below, this
    // covers the gap before that re-render lands).
    if (isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await submitAssessment({
        sessionId,
        basicInfo,
        goal,
        lifestyle,
        activity,
        medical,
        foodPreferences,
        likes,
        dislikes,
        mealSchedule,
        womensHealth,
        isFemale,
      });

      // Fires the exact 'assessment_complete' event FSD Screen 12
      // specifies — now that the assessment is actually saved, not just
      // that the button was clicked.
      analytics.trackGeneratePlanClick(sessionId);
      onNext?.();
    } catch (err) {
      analytics.trackGeneratePlanSubmitFailed(sessionId, err.message);
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isValid,
    isSubmitting,
    analytics,
    sessionId,
    errors,
    basicInfo,
    goal,
    lifestyle,
    activity,
    medical,
    foodPreferences,
    likes,
    dislikes,
    mealSchedule,
    womensHealth,
    isFemale,
    onNext,
  ]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Review your answers"
      description="Everything looks good? Edit any section, or generate your plan."
      onPrevious={handlePrevious}
      onNext={handleGeneratePlan}
      nextDisabled={!isValid || isSubmitting}
      nextLabel={isSubmitting ? "Generating…" : "Generate Diet Plan"}
    >
      <SectionStack>
        {visibleSections.map((section) => {
          const data = dataByKey[section.dataKey];
          const rows = data ? (section.formatter ? section.formatter(data) : autoFormatFields(data)) : [];

          return (
            <SectionCard key={section.key}>
              <SectionHeader>
                <SectionTitle>{section.title}</SectionTitle>
                <EditButton type="button" onClick={() => handleEditSection(section)}>
                  Edit
                </EditButton>
              </SectionHeader>

              {rows.length > 0 ? (
                <RowGrid>
                  {rows.map((row) => (
                    <Row key={row.label}>
                      <RowLabel>{row.label}</RowLabel>
                      <RowValue>{row.value}</RowValue>
                    </Row>
                  ))}
                </RowGrid>
              ) : (
                <EmptyState>Not completed yet.</EmptyState>
              )}
            </SectionCard>
          );
        })}
      </SectionStack>

      {!isValid && (
        <ValidationNotice role="alert">
          A few sections still need attention before your plan can be generated — use Edit above
          to finish them.
        </ValidationNotice>
      )}

      {submitError && (
        <ApiErrorNotice role="alert">
          {submitError} You can try Generate Diet Plan again.
        </ApiErrorNotice>
      )}
    </AssessmentStepShell>
  );
};

SummaryStep.displayName = "SummaryStep";
export default React.memo(SummaryStep);